import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';
import { useState } from 'react';
import { useSEOScript } from '@/hooks/useSEOScript';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const allArticles = getArticlesByCategory(slug || '');
  const [visibleCount, setVisibleCount] = useState(6);

  usePageMeta(
    category ? `${category.name}资讯` : '分类',
    category ? category.description : '乐鱼体育新闻资讯站分类页'
  );

  useSEOScript(category ? {
    type: 'CollectionPage',
    data: {
      name: `${category.name} - 乐鱼体育新闻资讯站`,
      description: category.description,
      url: `https://www.leyusports.com/category/${category.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '乐鱼体育新闻资讯站',
        url: 'https://www.leyusports.com',
      },
    },
  } : null);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const visibleArticles = allArticles.slice(0, visibleCount);
  const hasMore = visibleCount < allArticles.length;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="w-full bg-background-100/50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
          <div className="w-full px-4 md:px-6 pb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${category.icon} text-xl text-primary-600 w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{category.name}</h1>
              </div>
            </div>
            <p className="text-sm text-foreground-500 ml-[52px]">{category.description}</p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {allArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => setVisibleCount((prev) => prev + 6)}
                    className="inline-flex items-center gap-1.5 px-8 py-2.5 text-sm font-medium rounded-full border border-primary-300 text-primary-700 hover:bg-primary-50 transition-colors whitespace-nowrap"
                  >
                    加载更多
                    <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}